/**
 * Admin Authentication API Service
 * Handles password-based admin authentication with RBAC support
 */

import axios from 'axios';
import { getAdminEndpoint } from '../endpoints/adminEndpointManager';
import { getEnvironmentConfig } from '../config/apiConfig';
import {
  setAdminToken,
  setAdminUser,
  setAdminSession,
  clearAdminAuthData,
  getAdminToken
} from '../auth/authManager';
import { setConfigEndpoint } from '../endpoints/configEndpointManager';
import { storeEndpoints, setSelectedEndpoint, getEndpointOptions } from '../endpoints/endpointStore';

// Helper to get admin API key safely
const getAdminApiKey = () => {
  const config = getEnvironmentConfig();
  return config?.apiConfig?.ADMIN_API_KEY || '';
};

// Get auth service endpoint from stored configuration
const getAuthServiceEndpoint = () => {
  // First try to get from environment config (this is the auth service URL selected during login)
  const config = getEnvironmentConfig();
  if (config?.authServiceEndpoint) {
    return config.authServiceEndpoint;
  }
  // Fallback to admin endpoint
  return getAdminEndpoint();
};

// Create axios instance for auth service
const createAuthServiceClient = () => {
  const baseURL = `${getAuthServiceEndpoint()}/api/admin/auth`;

  // Log full URL for debugging
  console.log('[AdminAuth] Auth service URL:', baseURL);

  return axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: 10000,
  });
};

// Create axios instance for admin management
const createAdminManagementClient = () => {
  const baseURL = `${getAuthServiceEndpoint()}/api/admin`;

  // Log full URL for debugging
  console.log('[AdminAuth] Management service URL:', baseURL);

  return axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: 10000,
  });
};

export const adminAuthAPI = {
  /**
   * Check if initial setup is required (no admins exist)
   */
  checkSetupStatus: async () => {
    try {
      const client = createAuthServiceClient();
      const fullUrl = client.defaults.baseURL + '/setup-status';
      console.log('[AdminAuth] Checking setup status at:', fullUrl);
      const response = await client.get('/setup-status');
      return response.data;
    } catch (error) {
      console.error('[AdminAuth] Failed to check setup status:', error.message);
      console.log('[AdminAuth] Full URL attempted:', error.config?.baseURL + '/setup-status');
      throw error;
    }
  },

  /**
   * Initial setup - create first super admin
   * Only available when no admins exist
   */
  setup: async (adminData) => {
    try {
      const client = createAuthServiceClient();
      const response = await client.post('/setup', {
        ...adminData,
        // Truncate deviceName to 100 characters as per backend validation
        deviceName: navigator.userAgent.substring(0, 100)
      });

      if (response.data.token) {
        // Store auth data
        setAdminToken(response.data.token);
        setAdminUser(response.data.admin);
        setAdminSession(response.data.session);
      }

      return response.data;
    } catch (error) {
      console.error('Setup failed:', error);
      throw error;
    }
  },

  /**
   * Admin login with username/email and password
   */
  login: async (credentials) => {
    try {
      const client = createAuthServiceClient();
      const response = await client.post('/login', {
        usernameOrEmail: credentials.usernameOrEmail,
        password: credentials.password,
        sessionDuration: credentials.sessionDuration || 24,
        // Truncate deviceName to 100 characters as per backend validation
        deviceName: (credentials.deviceName || navigator.userAgent).substring(0, 100)
      });

      if (response.data.token) {
        // Store auth data
        setAdminToken(response.data.token);
        setAdminUser(response.data.admin);
        setAdminSession(response.data.session);

        // 🔐 Fetch config service endpoints from auth service
        console.log('[AdminAuth] Login successful, fetching endpoints...');
        try {
          // Use admin management client (baseURL: /api/admin) instead of auth service client (baseURL: /api/admin/auth)
          const managementClient = createAdminManagementClient();
          const token = getAdminToken();
          const endpointsResponse = await managementClient.get('/endpoints', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'x-api-key': getAdminApiKey()
            }
          });
          console.log('[AdminAuth] Endpoints response:', endpointsResponse);
          console.log('[AdminAuth] Endpoints response.data:', endpointsResponse.data);

          let endpoints = [];

          // Handle different response formats
          if (Array.isArray(endpointsResponse.data)) {
            // Response is directly an array
            endpoints = endpointsResponse.data;
          } else if (endpointsResponse.data && Array.isArray(endpointsResponse.data.endpoints)) {
            // Response is { endpoints: [...] }
            endpoints = endpointsResponse.data.endpoints;
          } else if (typeof endpointsResponse.data === 'string') {
            // Response is a JSON string
            try {
              const parsed = JSON.parse(endpointsResponse.data);
              endpoints = Array.isArray(parsed) ? parsed : (parsed.endpoints || []);
            } catch (e) {
              endpoints = [];
            }
          }

          console.log('[AdminAuth] Parsed endpoints:', endpoints);

          if (endpoints.length > 0) {
            // Store all endpoints in endpoint store
            console.log('[AdminAuth] About to call storeEndpoints with:', endpoints.length, 'endpoints');
            try {
              storeEndpoints(endpoints);
              console.log('[AdminAuth] ✅ storeEndpoints completed. Verifying write...');
              console.log('[AdminAuth] localStorage public-dns-endpoints-list AFTER storeEndpoints:', localStorage.getItem('public-dns-endpoints-list'));
            } catch (storeError) {
              console.error('[AdminAuth] ❌ FAILED to store endpoints:', storeError);
            }

            // Set the first endpoint as selected by default
            const firstEndpoint = endpoints[0];
            console.log('[AdminAuth] First endpoint object:', JSON.stringify(firstEndpoint));
            console.log('[AdminAuth] First endpoint.baseUrl:', firstEndpoint?.baseUrl);
            console.log('[AdminAuth] First endpoint.url:', firstEndpoint?.url);
            const endpointUrl = firstEndpoint?.baseUrl || firstEndpoint?.url || '';
            console.log('[AdminAuth] Extracted endpointUrl:', endpointUrl);
            if (endpointUrl) {
              console.log('[AdminAuth] About to call setSelectedEndpoint with:', endpointUrl);
              setSelectedEndpoint(endpointUrl);
              console.log('[AdminAuth] ✅ setSelectedEndpoint completed. Verifying config write...');
              console.log('[AdminAuth] localStorage config-service-endpoint AFTER setSelectedEndpoint:', localStorage.getItem('config-service-endpoint'));
            } else {
              console.error('[AdminAuth] ❌ Could not extract URL from endpoint object!');
            }

            // Log available options
            const options = getEndpointOptions();
            console.log('[AdminAuth] Available endpoints for dropdown:', options.map(o => o.label));
          } else {
            console.warn('[AdminAuth] No endpoints found in response');
          }
        } catch (endpointsError) {
          console.error('[AdminAuth] Failed to fetch endpoints:', endpointsError.message);
        }

        // 🔐 Fetch sub-role permissions for non-super admins
        const adminData = response.data.admin;
        if (!adminData.isSuperAdmin && adminData.role !== 'super_admin') {
          console.log('[AdminAuth] Non-super admin logged in, fetching sub-role permissions...');
          try {
            // Import subRoleAPI dynamically to avoid circular dependencies
            const { default: subRoleAPI } = await import('./subRoleAPI');
            
            // Fetch current admin permissions from sub-role system
            const permissionsResponse = await subRoleAPI.getCurrentAdminPermissions();
            console.log('[AdminAuth] Sub-role permissions fetched:', permissionsResponse);
            
            if (permissionsResponse.data) {
              // Update admin user with nested permissions
              const updatedAdmin = {
                ...adminData,
                permissions: permissionsResponse.data
              };
              setAdminUser(updatedAdmin);
              console.log('[AdminAuth] ✅ Admin permissions updated from sub-role system');
            }
          } catch (permissionError) {
            console.error('[AdminAuth] Failed to fetch sub-role permissions:', permissionError.message);
            // Don't fail login if permissions fetch fails, admin will have default permissions
          }
        }
      }

      return response.data;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  },

  /**
   * Admin logout
   */
  logout: async () => {
    try {
      const token = getAdminToken();
      if (!token) {
        clearAdminAuthData();
        return { message: 'Logout successful' };
      }

      const client = createAuthServiceClient();
      await client.post('/logout', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      clearAdminAuthData();
      return { message: 'Logout successful' };
    } catch (error) {
      console.error('Logout failed:', error);
      // Clear local data anyway
      clearAdminAuthData();
      throw error;
    }
  },

  /**
   * Get current admin profile
   */
  getProfile: async () => {
    try {
      const token = getAdminToken();
      if (!token) throw new Error('No token found');

      const client = createAuthServiceClient();
      const response = await client.get('/me', {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data;
    } catch (error) {
      console.error('Failed to get profile:', error);
      throw error;
    }
  },

  /**
   * Change password
   */
  changePassword: async (passwordData) => {
    try {
      const token = getAdminToken();
      if (!token) throw new Error('No token found');

      const client = createAuthServiceClient();
      const response = await client.post('/change-password', passwordData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data;
    } catch (error) {
      console.error('Failed to change password:', error);
      throw error;
    }
  },

  /**
   * Get active sessions
   */
  getSessions: async () => {
    try {
      const token = getAdminToken();
      if (!token) throw new Error('No token found');

      const client = createAuthServiceClient();
      const response = await client.get('/sessions', {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data;
    } catch (error) {
      console.error('Failed to get sessions:', error);
      throw error;
    }
  },

  /**
   * Logout from all other sessions
   */
  logoutAllOthers: async () => {
    try {
      const token = getAdminToken();
      if (!token) throw new Error('No token found');

      const client = createAuthServiceClient();
      const response = await client.post('/logout-all', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data;
    } catch (error) {
      console.error('Failed to logout from other sessions:', error);
      throw error;
    }
  },

  /**
   * Refresh admin user data from server
   */
  refreshAdminUser: async () => {
    try {
      const token = getAdminToken();
      if (!token) throw new Error('No token found');

      const client = createAuthServiceClient();
      const response = await client.get('/me', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.admin) {
        setAdminUser(response.data.admin);
      }

      return response.data.admin;
    } catch (error) {
      console.error('Failed to refresh admin user:', error);
      throw error;
    }
  }
};

export default adminAuthAPI;
